import { useCallback, useEffect, useState } from "react";
import { ActivityTimeline } from "../../components/ActivityTimeline";
import { InfoIcon, LockIcon, StopIcon } from "../../components/Icons";
import { PresenceRail } from "../../components/PresenceRail";
import type { SessionState, User } from "../../types";
import { HoldControl } from "./HoldControl";

function formatExpiry(timestamp: number | null) {
  if (!timestamp) return "Not armed";
  return new Intl.DateTimeFormat([], {
    hour: "numeric",
    minute: "2-digit"
  }).format(timestamp);
}

export function ControllerDashboard({
  user,
  state,
  relayConnected,
  send
}: {
  user: User;
  state: SessionState;
  relayConnected: boolean;
  send: (message: Record<string, unknown>) => boolean;
}) {
  const [intensity, setIntensity] = useState(1);

  useEffect(() => {
    if (intensity > state.limits.maxIntensity) {
      setIntensity(state.limits.maxIntensity);
    }
  }, [intensity, state.limits.maxIntensity]);

  const start = useCallback(
    () => send({ type: "hold:start", intensity }),
    [intensity, send]
  );
  const heartbeat = useCallback(
    (holdId: string) => send({ type: "hold:heartbeat", holdId }),
    [send]
  );
  const stop = useCallback(
    () => send({ type: "hold:stop" }),
    [send]
  );

  const ready =
    relayConnected &&
    state.armed &&
    state.presence.wearer &&
    state.device.outputReady;
  const disabledReason = !relayConnected
    ? "Private relay is reconnecting."
    : !state.presence.wearer
      ? "The wearer must be present."
      : !state.device.outputReady
        ? state.device.mode === "diagnostic"
          ? "Real output is locked until the collar protocol is verified."
          : "The wearer must connect a bridge."
        : !state.armed
          ? "The wearer has not armed this session."
          : "";

  return (
    <div className="dashboard">
      <PresenceRail
        state={state}
        role={user.role}
        relayConnected={relayConnected}
      />
      <main className="controller-main">
        <section className="controller-canvas">
          <header className="page-heading">
            <h1>Shared session</h1>
            <p className={state.armed ? "connected-color" : ""}>
              <span className={`status-dot ${state.armed ? "" : "status-dot--off"}`} />
              {state.armed ? "Session armed" : "Waiting for the wearer"}
            </p>
            <small>
              {state.armed
                ? "The wearer has set the limits. Hold only within those boundaries."
                : disabledReason}
            </small>
          </header>

          <div className="mobile-limit-summary" aria-label="Wearer limits">
            <span>
              Intensity cap
              <strong>{state.limits.maxIntensity}</strong>
            </span>
            <span>
              Hold cap
              <strong>{state.limits.maxHoldMs / 1_000}s</strong>
            </span>
            <span>
              Expires
              <strong>{formatExpiry(state.sessionEndsAt)}</strong>
            </span>
          </div>

          <HoldControl
            hold={state.hold}
            maxHoldMs={state.limits.maxHoldMs}
            disabled={!ready}
            onStart={start}
            onHeartbeat={heartbeat}
            onStop={stop}
          />

          <div className="controller-controls">
            <label className="intensity-control">
              <span>Intensity</span>
              <strong>{intensity} <small>of 10</small></strong>
              <span className="range-row">
                <b>1</b>
                <input
                  type="range"
                  min="1"
                  max={Math.max(1, state.limits.maxIntensity)}
                  step="1"
                  value={intensity}
                  onChange={(event) => setIntensity(Number(event.target.value))}
                  disabled={
                    !state.armed ||
                    Boolean(state.hold) ||
                    state.limits.maxIntensity === 1
                  }
                  aria-label="Intensity within wearer limit"
                />
                <b>10</b>
              </span>
              <small>Wearer maximum {state.limits.maxIntensity}</small>
            </label>
            <div className="hold-allowance">
              <span>Maximum single hold</span>
              <strong>{state.limits.maxHoldMs / 1_000} <small>seconds</small></strong>
              <small>Release sooner whenever there is doubt.</small>
            </div>
          </div>
        </section>

        <aside className="limits-rail">
          <header>
            <h2>Wearer’s limits</h2>
            <LockIcon />
          </header>
          <Limit label="Maximum intensity" value={String(state.limits.maxIntensity)} />
          <Limit label="Maximum hold" value={`${state.limits.maxHoldMs / 1_000} sec`} />
          <Limit label="Session expires" value={formatExpiry(state.sessionEndsAt)} />
          <p>Limits are set by the wearer and cannot be changed here.</p>
        </aside>

        <ActivityTimeline events={state.events} compact />
      </main>

      <div className="safety-strip">
        <div>
          <StopIcon />
          <span><strong>Safety first</strong>Control stops on release, disconnect, or timeout.</span>
        </div>
        <div>
          <InfoIcon />
          <span>If anything doesn’t feel right, stop and check in.</span>
        </div>
        <button
          className="button button--danger"
          onClick={() => send({ type: "session:end" })}
          disabled={!state.armed && !state.hold}
        >
          <StopIcon /> END SESSION
        </button>
      </div>
    </div>
  );
}

function Limit({ label, value }: { label: string; value: string }) {
  return (
    <div className="limit">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
